/**
 * 场景分析页面 — SceneAnalysis（二期批2 翻新，对齐概念稿 scenes.html）。
 *
 * GET /api/products/{id}          产品名（标题/面包屑）
 * GET /api/products/{id}/scenes   场景列表（intent/question/ai_platforms/priority/is_valid/round/keywords）
 *
 * 结构：产品子页签（概览/场景分析/素材库/事实包/GHS 诊断）→ 只读说明 →
 *       4 指标卡 → 场景卡网格 → 覆盖说明卡。
 * 场景为 GEO 问题场景（用户向 AI 的提问意图），由规划引擎产出，网站只读。
 *
 * 概念稿裁剪：场景描述（人群画像）/关联话题数/可见性分值无接口字段，跳过不虚构；
 * 覆盖平台 x/5、P0 数、有效性、轮次、关键词均为接口真实字段。
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { QuizOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import { getProductDetail, getProductScenes } from "../api/products";
import { useCompanyStore } from "../store/companyStore";
import { INTENT_MAP, PLATFORM_MAP, PRIORITY_MAP, platformLabel } from "../utils/constants";
import type { SceneAnalysis as SceneAnalysisType } from "../types";

/** 平台全集（覆盖分母，对齐 PLATFORM_MAP 五家）。 */
const ALL_PLATFORMS = Object.keys(PLATFORM_MAP).length;

/** 优先级徽章。 */
function PriorityBadge({ priority }: { priority: string }) {
  const info = PRIORITY_MAP[priority] || {
    label: priority,
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function SceneAnalysis() {
  const { productId } = useParams<{ productId: string }>();
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);
  const effectiveProductId = productId || selectedProductId;

  const [productName, setProductName] = useState("");
  const [scenes, setScenes] = useState<SceneAnalysisType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  /** 产品名（标题用，失败静默）。 */
  useEffect(() => {
    if (!effectiveProductId) return;
    getProductDetail(effectiveProductId)
      .then((d) => setProductName(d.product?.name || ""))
      .catch(() => setProductName(""));
  }, [effectiveProductId]);

  /** 场景列表。 */
  useEffect(() => {
    if (!effectiveProductId) {
      setScenes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    getProductScenes(effectiveProductId)
      .then(setScenes)
      .catch((err) => {
        setError(err instanceof Error ? err.message : "获取场景分析失败");
        setScenes([]);
      })
      .finally(() => setLoading(false));
  }, [effectiveProductId]);

  /** 覆盖聚合。 */
  const coverage = useMemo(() => {
    const valid = scenes.filter((s) => s.is_valid);
    const platformSet = new Set<string>();
    for (const s of scenes) {
      for (const p of s.ai_platforms || []) platformSet.add(p);
    }
    const p0 = scenes.filter((s) => s.priority === "P0").length;
    return {
      total: scenes.length,
      valid: valid.length,
      p0,
      platforms: platformSet.size,
      platformNames: [...platformSet].map((p) => platformLabel(p)),
    };
  }, [scenes]);

  if (!effectiveProductId) {
    return (
      <Box>
        <PageHeader
          title="场景分析"
          breadcrumbs={[
            { label: "资产管理" },
            { label: "产品", path: "/products" },
            { label: "场景分析" },
          ]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先选择一个产品"
              subtitle="从产品列表进入，或使用页面顶部的产品选择器"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={productName ? `场景分析 · ${productName}` : "场景分析"}
        breadcrumbs={[
          { label: "资产管理" },
          { label: "产品", path: "/products" },
          ...(productName
            ? [{ label: productName, path: `/products/${effectiveProductId}` }]
            : []),
          { label: "场景分析" },
        ]}
        subtitle="GEO 问题场景覆盖视图 · 场景-平台-优先级"
      />

      {/* 产品子页签（对齐概念稿 tabs） */}
      <Tabs
        value={1}
        sx={{ mb: 2, borderBottom: "1px solid", borderColor: "divider" }}
      >
        <Tab
          label="概览"
          component={RouterLink}
          to={`/products/${effectiveProductId}`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="场景分析"
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="素材库"
          component={RouterLink}
          to={`/products/${effectiveProductId}/materials`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="事实包"
          component={RouterLink}
          to={`/products/${effectiveProductId}/fact-packs`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
        <Tab
          label="GHS 诊断"
          component={RouterLink}
          to={`/products/${effectiveProductId}/ghs`}
          sx={{ textTransform: "none", fontSize: 13, minHeight: 40 }}
        />
      </Tabs>

      <Alert severity="info" sx={{ mb: 2 }}>
        场景由规划引擎产出后入库展示；网站不开放新建 / 编辑 / AI 挖掘，话题 CRUD
        与额度管理在二期续建（PRD F8）。
      </Alert>

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="场景总数"
            value={loading ? "—" : coverage.total}
            subtitle={coverage.valid < coverage.total ? `有效 ${coverage.valid}` : "全部有效"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="P0 优先"
            value={loading ? "—" : coverage.p0}
            subtitle="最高优先级问题场景"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="覆盖平台"
            value={loading ? "—" : `${coverage.platforms} / ${ALL_PLATFORMS}`}
            subtitle={coverage.platformNames.join(" / ") || undefined}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="有效场景"
            value={loading ? "—" : coverage.valid}
            subtitle={
              coverage.total > 0
                ? `有效率 ${((coverage.valid / coverage.total) * 100).toFixed(0)}%`
                : undefined
            }
          />
        </Grid>
      </Grid>

      {/* 场景卡网格 */}
      {loading ? (
        <Loading fullPage />
      ) : error ? (
        <Card>
          <CardContent>
            <EmptyState text={error} />
          </CardContent>
        </Card>
      ) : scenes.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              text="暂无场景分析"
              source="由规划引擎产出 · 分析后自动展示"
            />
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {scenes.map((scene) => {
              const intentLabel = INTENT_MAP[scene.intent] || scene.intent;
              const platformCount = scene.ai_platforms?.length || 0;
              return (
                <Grid item xs={12} md={6} key={scene.id}>
                  <Card sx={{ height: "100%" }}>
                    <CardContent>
                      {/* 卡头：问题 + 有效性 */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <QuizOutlined
                            sx={{ color: "primary.main", fontSize: 18, mt: 0.2 }}
                          />
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {scene.question}
                          </Typography>
                        </Box>
                        <Badge
                          label={scene.is_valid ? "有效" : "无效"}
                          variant={scene.is_valid ? "success" : "error"}
                        />
                      </Box>

                      {/* 徽标行：意图 / 优先级 / 轮次 */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
                        <Badge label={intentLabel} variant="info" />
                        <PriorityBadge priority={scene.priority} />
                        <Badge label={`第 ${scene.analysis_round} 轮`} />
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      {/* 覆盖平台 */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: scene.keywords && scene.keywords.length > 0 ? 1 : 0,
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 56 }}>
                          覆盖平台
                        </Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                          {platformCount} / {ALL_PLATFORMS}
                        </Typography>
                        {scene.ai_platforms && scene.ai_platforms.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {scene.ai_platforms.map((platform) => (
                              <Chip
                                key={platform}
                                label={platformLabel(platform)}
                                size="small"
                                sx={{
                                  fontSize: 11,
                                  height: 20,
                                  bgcolor: "brand.50",
                                  color: "primary.main",
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>

                      {/* 关键词 */}
                      {scene.keywords && scene.keywords.length > 0 && (
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 56 }}>
                            关键词
                          </Typography>
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {scene.keywords.map((keyword, i) => (
                              <Chip
                                key={i}
                                label={keyword}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 20 }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          {/* 覆盖说明 */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 1 }}>
                覆盖说明
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.8 }}>
                {coverage.total} 个问题场景（有效 {coverage.valid} 个，P0{" "}
                {coverage.p0} 个），探测覆盖 {coverage.platforms} / {ALL_PLATFORMS} 家
                AI 平台
                {coverage.platformNames.length > 0 &&
                  `（${coverage.platformNames.join(" / ")}）`}
                。各场景的平台级可见性表现见「AI 可见性」全景矩阵。
              </Typography>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
